import clsx from "clsx";
import type { HTMLAttributes, ReactNode } from "react";

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  /** Tighter padding for dense lists. */
  compact?: boolean;
  /** Element to render as — use `"div"` for an interactive card (`role="button"`). */
  as?: "section" | "div" | "article";
}

export function Card({
  title,
  description,
  actions,
  footer,
  compact = false,
  as: Component = "section",
  className,
  children,
  ...rest
}: CardProps) {
  return (
    <Component
      className={clsx("surface print-plain", compact ? "p-4" : "p-6", className)}
      {...rest}
    >
      {(title || actions) && (
        <header className="mb-4 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div>
            {title && (
              <h2 className="font-display text-lg text-ink-900 dark:text-ink-50">{title}</h2>
            )}
            {description && (
              <p className="mt-1 text-sm text-ink-600 dark:text-ink-400">{description}</p>
            )}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      {children}
      {footer && (
        <footer className="mt-4 border-t border-ink-200 pt-4 dark:border-ink-800">{footer}</footer>
      )}
    </Component>
  );
}
